import { useState } from 'react';
import { useRecipients, useSendReport } from '../../../hooks/useSalesResearch';

interface Props {
  runId: string;
  companyName: string;
  onClose: () => void;
}

export function SendReportDialog({ runId, companyName, onClose }: Props) {
  const recipients = useRecipients(true);
  const send = useSendReport(runId);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');

  const people = recipients.data?.recipients ?? [];
  const chosen = people.filter((p) => selected.has(p.email));

  const toggle = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const submit = () => {
    if (chosen.length === 0) return;
    send.mutate({
      recipients: chosen.map((p) => ({ email: p.email, name: p.name })),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  };

  const failures = send.data?.outcomes.filter((o) => o.status === 'failed') ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-primary p-5 space-y-4">
        <div>
          <h3 className="text-body-md font-semibold">Send “{companyName}” brief</h3>
          <p className="text-body-sm text-text-tertiary">
            Sent as a Slack DM from you, with the snapshot, triggers and outreach angle in the
            message and a link to the full brief.
          </p>
        </div>

        {recipients.isLoading ? (
          <p className="text-body-sm text-text-tertiary">Loading people…</p>
        ) : recipients.error instanceof Error ? (
          <p className="text-body-sm text-error-dark">{recipients.error.message}</p>
        ) : (
          <div className="max-h-52 space-y-1 overflow-y-auto rounded-md border border-border p-2">
            {people.map((p) => (
              <label key={p.id} className="flex min-w-0 cursor-pointer items-center gap-2 text-body-sm">
                <input
                  type="checkbox"
                  checked={selected.has(p.email)}
                  onChange={() => toggle(p.email)}
                />
                <span className="whitespace-nowrap font-medium">{p.name}</span>
                {/* The dialog is narrower than "name + full sequesto.com
                    address", so the address gives way rather than wrapping the
                    name onto a second line. */}
                <span className="truncate text-text-tertiary">{p.email}</span>
              </label>
            ))}
          </div>
        )}

        <label className="block">
          <span className="text-body-sm font-medium">Note (optional)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Why you're sending this"
            className="mt-1 w-full rounded-md border border-border bg-surface-primary px-3 py-2 text-body-sm"
          />
        </label>

        {send.data && (
          <div
            className={`rounded-md border px-3 py-2 text-body-sm ${
              failures.length > 0
                ? 'border-warning/20 bg-warning-light text-warning-dark'
                : 'border-success/20 bg-success-light text-success-dark'
            }`}
          >
            Sent to {send.data.sent} of {send.data.sent + send.data.failed}.
            {/* A partial send must name who didn't get it — SES rejecting an
                unverified recipient is the common case here. */}
            {failures.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {failures.map((f) => (
                  <li key={f.email} className="text-body-xs">
                    {f.email}: {f.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {send.error instanceof Error && (
          <p className="text-body-sm text-error-dark">{send.error.message}</p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-3 py-1.5 text-body-sm hover:bg-surface-secondary"
          >
            {send.data ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={chosen.length === 0 || send.isPending}
            className="rounded-md bg-surface-inverse px-3 py-1.5 text-body-sm font-medium text-text-inverse disabled:opacity-50"
          >
            {send.isPending ? 'Sending…' : `Send to ${chosen.length || ''}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
}
